<template>
  <FormContainer withoutDivider>
    <template #title>Liste Salarié(e)s</template>
    <template #header-actions>
      <BasicButton
        width="auto"
        size="large"
        variant="outlined"
        :label="!nbMaxSalariésAtteint ? 'Ajouter un salarié' : 'Nombre maximum de salariés atteint'"
        :icon-name="!nbMaxSalariésAtteint ? 'add' : 'danger'"
        @click="ajouterSalarié"
        :disabled="ajoutSalarieDisabled"
      />
    </template>
    <template
      #content
      v-if="listeSalariés && listeSalariés.length"
    >
      <WrapperCheckbox
        v-if="typeContextCourant === TypesContextesAutorisesEnum.Prestataire"
        id="field_attestationSalariesSaisies"
        v-model="attestationSalariesSaisiesModel"
        :alertLabel="meta.attestationSalariesSaisies?.$messageErreurDirty!"
        label="Dans le cadre du RGPD (articles 5 et 6), j'atteste que les informations « salariés » saisies sont issues de la convention de formation"
        :readonly
      />

      <!-- Desktop version -->
      <div class="elements--desktop">
        <div class="cardLayoutWrapper cardLayoutWrapper--header">
          <div class="elem elem--span-2"></div>
          <div class="elem elem--span-6">
            <span>Sexe</span>
          </div>
          <div class="elem elem--span-6">
            <span>Nom</span>
          </div>
          <div class="elem elem--span-6">
            <span>Prénom</span>
          </div>
          <div class="elem elem--span-8">
            <span>Date de naissance</span>
          </div>
          <div class="elem elem--span-8">
            <span>Email</span>
          </div>
        </div>

        <div
          v-for="(salarié, index) in listeSalariés"
          class="listCardDesktopWrapper"
          :key="index"
        >
          <div
            class="cardLayoutWrapper cardLayoutWrapper--selectionnable"
            :class="{ isActive: activeSalariéIndex === index }"
            @click="définirSalariéEnCours(index)"
          >
            <BasicAlert
              :has-bg="false"
              :alert-type="!meta.salaries?.at(index)?.$estErreur ? 'success' : 'danger'"
              class="elem elem--span-2"
            />
            <div class="elem elem--span-6">
              <span>{{ salarié.sexe }}</span>
            </div>
            <div class="elem elem--span-6">
              <span>{{ salarié.nomNaissance }}</span>
            </div>
            <div class="elem elem--span-6">
              <span>{{ salarié.prenom }}</span>
            </div>
            <div class="elem elem--span-8">
              <span>{{ dateFormatter(salarié.dateNaissance) }}</span>
            </div>
            <div class="elem elem--span-8">
              <span>{{ salarié.email }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile version -->
      <div class="elements--mobile">
        <div
          v-for="(salarie, index) in listeSalariés"
          :key="index"
          :class="['card', { 'card--active': activeSalariéIndex === index }]"
          @click="définirSalariéEnCours(index)"
        >
          <BasicText weight="bold">#{{ index + 1 }} {{ salarie.nom }} {{ salarie.prenom }}</BasicText>
          <div class="card__content">
            <BasicText wrap>
              <BasicText weight="bold">Sexe :</BasicText>
              {{ salarie.sexe }}
            </BasicText>
            <BasicText wrap>
              <BasicText weight="bold">Nom :</BasicText>
              {{ salarie.nomNaissance }}
            </BasicText>
            <BasicText wrap>
              <BasicText weight="bold">Prénom :</BasicText>
              {{ salarie.prenom }}
            </BasicText>
            <BasicText wrap>
              <BasicText weight="bold">Date de naissance :</BasicText>
              {{ dateFormatter(salarie.dateNaissance) }}
            </BasicText>
            <BasicText wrap>
              <BasicText weight="bold">Email :</BasicText>
              {{ salarie.email }}
            </BasicText>
          </div>
        </div>
      </div>
    </template>
    <template
      #content
      v-else
    >
      <!-- Empty state -->
      <BasicAlert
        v-if="meta.salaries && meta.salaries.$estErreurDirty"
        :alertLabel="meta.salaries.$messageErreurDirty!"
      />
      <EmptyDataTable>
        <template #content>
          Retrouvez ici la
          <strong>liste des salarié(e)s que vous avez ajoutés</strong>
          .
        </template>
      </EmptyDataTable>
    </template>
  </FormContainer>
</template>

<script setup lang="ts">
  import { computed, inject } from 'vue'
  import { storeToRefs } from 'pinia'
  import { TypesContextesAutorisesEnum } from '@/features/auth/context-app'
  import { SalarieActionKey, type SalarieAction } from '@/features/interface/demande/af/steps/salarie/useSalarieActions'
  import { useDemandeAFStore } from '@/features/interface/demande/af/useDemandeAFStore'
  import { EmptyDataTable, FormContainer } from '@/features/interface/demande/shared'
  import { dateFormatter } from '@/features/shared/tools'

  const store = useDemandeAFStore()
  const { setupFieldWithKey } = store
  const { readonly, meta, typeContextCourant } = storeToRefs(store)

  const attestationSalariesSaisiesModel = setupFieldWithKey<boolean>('attestationSalariesSaisies', true)

  const { ajouterSalarié, nbMaxSalariésAtteint, activeSalariéIndex, listeSalariés, définirSalariéEnCours } =
    inject<SalarieAction>(SalarieActionKey)!

  const ajoutSalarieDisabled = computed<boolean>(() => {
    return nbMaxSalariésAtteint.value || readonly.value
  })
</script>
